import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { PaginatedResponse, PaginatorFilters } from '@michelin/theme';
import { EnvConfigurationService } from '../envconf/envconf.service';
import { ApiService } from '../api/api.service';
import { Pagination } from '../../models/pagination';
import { OrderDto, OrderNoIdDto } from '../../models/order/order-dto';
import { InvoiceDto, InvoiceNoIdDto } from '../../models/order/invoice-dto';
import {
  AutomakerCommissionValueDto,
  OrderAttendanceReportDto,
  OrderDeliveryReportDto,
  OrderItemAttendanceReportDto
} from '../../models/dashboard/dashboard-dto';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  endpoint = `${EnvConfigurationService.configuration.BACKEND_URL}${EnvConfigurationService.configuration.BACKEND_SUB_PATH}/orders`;

  constructor(private readonly http: HttpClient, private readonly apiService: ApiService) {}

  getPaginatedResponse(pagination: Pagination<OrderDto>): PaginatedResponse<OrderDto> {
    return {
      count: pagination.totalElements,
      items: pagination.content
    };
  }

  getOrderItemAttendanceReportPaginatedResponse(
    pagination: Pagination<OrderItemAttendanceReportDto>
  ): PaginatedResponse<OrderItemAttendanceReportDto> {
    return {
      count: pagination.totalElements,
      items: pagination.content
    };
  }

  search(paginatorFilters?: PaginatorFilters): Observable<PaginatedResponse<OrderDto>> {
    return this.http
      .get<PaginatedResponse<OrderDto>>(`${this.endpoint}/search${this.apiService.getQueryParams(paginatorFilters)}`)
      .pipe(map((items: Pagination<OrderDto>) => this.getPaginatedResponse(items)));
  }

  findAll(): Observable<OrderDto[]> {
    return this.http.get<OrderDto[]>(this.endpoint);
  }

  findById(orderId: number): Observable<OrderDto> {
    return this.http.get<OrderDto>(`${this.endpoint}/${orderId}`);
  }

  update(orderId: number, data: OrderNoIdDto): Observable<OrderDto> {
    return this.http.put<OrderDto>(`${this.endpoint}/${orderId}`, data);
  }

  uploadOrderItems(orderId: number, orderItemsFile: File): Observable<Object> {
    const data = new FormData();
    data.append('file', orderItemsFile);
    return this.http.post(`${this.endpoint}/orderItems/${orderId}/upload`, data);
  }

  findAllInvoices(orderId: number): Observable<Array<InvoiceDto>> {
    return this.http.get<Array<InvoiceDto>>(`${this.endpoint}/${orderId}/invoices`);
  }

  createInvoice(orderId: number, invoice: InvoiceNoIdDto): Observable<InvoiceDto> {
    return this.http.post<InvoiceDto>(`${this.endpoint}/${orderId}/invoices`, invoice);
  }

  uploadInvoiceFile(orderId: number, invoiceId: number, file: File): Observable<Object> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.endpoint}/${orderId}/invoices/${invoiceId}/upload`, formData);
  }

  downloadInvoiceFile(orderId: number, invoiceId: number) {
    window.open(`${this.endpoint}/${orderId}/invoices/${invoiceId}/download`, '_blank');
  }

  getReportParams(dateStart: string, dateEnd: string, distributorId?: number, automakerId?: number) {
    const params = new URLSearchParams();
    if (dateStart) params.set('dateStart', dateStart);
    if (dateEnd) params.set('dateEnd', dateEnd);
    if (typeof distributorId !== 'undefined') params.set('distributorId', distributorId.toString());
    if (typeof automakerId !== 'undefined') params.set('automakerId', automakerId.toString());

    return params;
  }

  getDeliveryReport(
    dateStart: string,
    dateEnd: string,
    distributorId?: number,
    automakerId?: number
  ): Observable<OrderDeliveryReportDto[]> {
    const params = this.getReportParams(dateStart, dateEnd, distributorId, automakerId);
    return this.http.get<OrderDeliveryReportDto[]>(`${this.endpoint}/reports/delivery?${params.toString()}`);
  }

  getAttendanceReport(
    dateStart: string,
    dateEnd: string,
    distributorId?: number,
    automakerId?: number
  ): Observable<OrderAttendanceReportDto[]> {
    const params = this.getReportParams(dateStart, dateEnd, distributorId, automakerId);
    return this.http.get<OrderAttendanceReportDto[]>(`${this.endpoint}/reports/attendance?${params.toString()}`);
  }

  getAutomakerCommissionValue(
    dateStart: string,
    dateEnd: string,
    distributorId?: number,
    automakerId?: number
  ): Observable<AutomakerCommissionValueDto> {
    const params = this.getReportParams(dateStart, dateEnd, distributorId, automakerId);
    return this.http.get<AutomakerCommissionValueDto>(`${this.endpoint}/reports/commission?${params.toString()}`);
  }

  cancelOrder(orderId: number): Observable<OrderDto> {
    return this.http.put<OrderDto>(`${this.endpoint}/${orderId}/cancelOrder`, null);
  }

  cancelItem(orderItemId: number): Observable<OrderDto> {
    return this.http.put<OrderDto>(`${this.endpoint}/orderItems/${orderItemId}/cancelItem`, null);
  }

  getOrderItemAttendanceReport(paginatorFilters?: PaginatorFilters): Observable<PaginatedResponse<OrderItemAttendanceReportDto>> {
    return this.http
      .get<PaginatedResponse<OrderItemAttendanceReportDto>>(
        `${this.endpoint}/reports/attendanceItems${this.apiService.getQueryParams(paginatorFilters)}`
      )
      .pipe(map((items: Pagination<OrderItemAttendanceReportDto>) => this.getOrderItemAttendanceReportPaginatedResponse(items)));
  }
}
